import type { ThreadMessageItem } from "hooks/use-email-thread-api";
import { cn } from "lib/utils";
import { Inbox, Send, X } from "lucide-react";
import type { FC } from "react";
import { formatShortDateTime } from "utils/relative-time";

export type EmailThreadMessageModalProps = {
  isOpen: boolean;
  message: ThreadMessageItem | null;
  onClose: () => void;
};

/**
 * スレッド内 1 メッセージを大画面モーダルで表示する。
 * @param {EmailThreadMessageModalProps} props isOpen / message / onClose
 * @return {JSX.Element|null} モーダル
 */
export const EmailThreadMessageModal: FC<EmailThreadMessageModalProps> = ({
  isOpen,
  message,
  onClose,
}) => {
  if (!isOpen || !message) return null;

  const isSent = message.direction === "sent";
  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-3xl">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            {isSent ? (
              <Send
                size={14}
                className="text-violet-700 shrink-0"
                aria-hidden
              />
            ) : (
              <Inbox
                size={14}
                className="text-slate-500 shrink-0"
                aria-hidden
              />
            )}
            <h3 className="font-bold text-base truncate">
              {message.subject || "(件名なし)"}
            </h3>
          </div>
          <button
            type="button"
            className="text-slate-500 hover:text-slate-700 shrink-0"
            onClick={onClose}
            aria-label="閉じる"
          >
            <X size={18} aria-hidden />
          </button>
        </div>

        <div
          className={cn(
            "rounded-lg border p-3 text-xs space-y-1 mb-3",
            isSent
              ? "border-violet-200 bg-violet-50"
              : "border-slate-200 bg-slate-50",
          )}
        >
          <div>
            <span className="text-base-content/60">From: </span>
            <span className="font-medium">
              {message.fromName
                ? `${message.fromName} <${message.fromEmail}>`
                : message.fromEmail}
            </span>
          </div>
          <div>
            <span className="text-base-content/60">To: </span>
            <span className="font-medium">
              {message.toEmails.length > 0
                ? message.toEmails.join(", ")
                : "(宛先不明)"}
            </span>
          </div>
          <div>
            <span className="text-base-content/60">日時: </span>
            <span className="font-mono">
              {formatShortDateTime(message.occurredAt)}
            </span>
          </div>
        </div>

        <pre className="whitespace-pre-wrap text-sm text-slate-800 max-h-[60vh] overflow-auto">
          {message.bodyText || "(本文なし)"}
        </pre>
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </div>
  );
};
